import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';

/**
 * CampoEnLinea — pulsar un dato lo convierte en campo. Enter guarda, Escape
 * cancela, un filete verde confirma.
 *
 * Sustituye al modal «Editar mi perfil público», que abría los TRES campos para
 * corregir uno. Aquí son datos de ficha que la gente arregla de uno en uno —una
 * extensión nueva, una habilidad más—, así que el modal cobraba el precio de un
 * formulario entero por cada cambio de una palabra.
 *
 * Cuatro cosas que no son opcionales y que casi siempre se olvidan:
 *
 *   · Es un `<button>`, no un `<div>` con onClick. Recibe foco, responde a Enter
 *     y el lector de pantalla lo anuncia como algo que se puede activar.
 *   · Al entrar en edición el foco va al campo y el texto queda seleccionado:
 *     lo más probable es reemplazar, no añadir al final.
 *   · Escape cancela y DEVUELVE el valor original. Sin esto, «cancelar» sería
 *     recargar la página.
 *   · Marca `window._appFormDirty` mientras hay cambios sin guardar, para que la
 *     protección de navegación que ya existe en `app.js` también lo cubra. Es la
 *     misma guarda que usan los modales vanilla; no se inventa otra.
 *
 * La confirmación es un filete verde bajo el valor durante 900 ms, no un aviso
 * flotante: avisar de algo que el usuario está mirando es ruido.
 */
export function CampoEnLinea({
  etiqueta, valor, icono, placeholder, multilinea, ayuda, onGuardar,
}: {
  etiqueta: string;
  valor: string;
  /** Clase de Bootstrap Icons. Decorativa: la etiqueta la lleva el texto. */
  icono: string;
  placeholder?: string;
  multilinea?: boolean;
  ayuda?: string;
  onGuardar: (nuevo: string) => Promise<unknown>;
}) {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(valor);
  const [guardando, setGuardando] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const campoRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Si el valor cambia por fuera (otra pestaña, una recarga de la consulta) y no
  // estamos editando, el borrador se pone al día. Si estamos editando NO se
  // toca: sobrescribir lo que alguien está escribiendo es imperdonable.
  useEffect(() => {
    if (!editando) setBorrador(valor);
  }, [valor, editando]);

  useEffect(() => {
    if (!editando) return;
    const el = campoRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(0, el.value.length);
  }, [editando]);

  const sucio = borrador !== valor;
  useEffect(() => {
    if (editando && sucio) window._appFormDirty = true;
    return () => { if (window._appFormDirty) window._appFormDirty = false; };
  }, [editando, sucio]);

  function cancelar() {
    setBorrador(valor);
    setError(null);
    setEditando(false);
    window._appFormDirty = false;
  }

  async function guardar() {
    if (!sucio) { setEditando(false); return; }
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(borrador.trim());
      window._appFormDirty = false;
      setEditando(false);
      setConfirmado(true);
      setTimeout(() => setConfirmado(false), 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  if (!editando) {
    return (
      <div className="group flex items-start gap-3 py-1.5">
        <i className={cn('bi', icono, 'mt-0.5 w-4 shrink-0 text-3 text-muted')} aria-hidden="true" />
        <button
          type="button"
          onClick={() => setEditando(true)}
          className={cn(
            'min-w-0 flex-1 rounded-xs px-1.5 py-1 text-left text-3 -mx-1.5',
            // El dato se pulsa para editarlo, así que es un objetivo táctil como
            // cualquier otro: 24 px de alto es media yema de dedo.
            'flex items-center pointer-coarse:min-h-11',
            'transition-colors duration-150 hover:bg-sunken',
            'focus-visible:focus-anillo',
            confirmado && 'shadow-[inset_0_-2px_0_0_var(--color-ok)]',
          )}
        >
          <span className="sr-only">{etiqueta}. Pulsa para editar. </span>
          {valor ? (
            <span className="whitespace-pre-wrap break-words text-ink">{valor}</span>
          ) : (
            <span className="text-muted">{placeholder ?? `Añadir ${etiqueta.toLowerCase()}`}</span>
          )}
          <i
            className="bi bi-pencil ml-2 text-1 text-muted opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden="true"
          />
        </button>
      </div>
    );
  }

  const Control = multilinea ? 'textarea' : 'input';
  return (
    <div className="flex items-start gap-3 py-1.5">
      <i className={cn('bi', icono, 'mt-2 w-4 shrink-0 text-3 text-muted')} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <label className="mb-1 block text-2 font-semibold text-ink">{etiqueta}</label>
        <Control
          ref={campoRef as never}
          value={borrador}
          rows={multilinea ? 3 : undefined}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setBorrador(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); cancelar(); }
            // En un campo de una línea, Enter guarda. En un área de texto no:
            // ahí Enter es un salto de párrafo y robarlo sería insufrible.
            if (e.key === 'Enter' && !multilinea) { e.preventDefault(); void guardar(); }
          }}
          className={cn(
            'w-full rounded-xs border border-field-border bg-surface px-3 py-2 text-3 text-ink',
            'focus:border-brand-ink focus:outline-none focus:ring-2 focus:ring-accent/35',
            multilinea && 'resize-y',
            error && 'border-danger ring-2 ring-danger/25',
          )}
        />
        {error ? (
          <p className="mt-1 text-2 text-danger-ink" role="alert">{error}</p>
        ) : ayuda ? (
          <p className="mt-1 text-2 text-muted">{ayuda}</p>
        ) : null}
        <div className="mt-2 flex items-center gap-2">
          <Button size="sm" loading={guardando} onClick={() => void guardar()}>Guardar</Button>
          <Button size="sm" variant="ghost" onClick={cancelar}>Cancelar</Button>
          <span className="text-1 text-muted">
            {multilinea ? 'Escape cancela' : 'Enter guarda · Escape cancela'}
          </span>
        </div>
      </div>
    </div>
  );
}
